/**
 * Chunk Error Boundary Component
 * 
 * Handles chunk loading errors that occur during dynamic imports.
 * When a new deployment invalidates old chunks, this component
 * catches the error and automatically reloads the page.
 * 
 * This prevents users from seeing error messages during deployments.
 */

import { Component, ReactNode } from 'react';
import { FullscreenLoading } from './LoadingSpinner';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isReloading: boolean;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isReloading: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if it's a chunk loading error
    const isChunkError = 
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk') ||
      error.message.includes('Importing a module script failed');

    if (isChunkError) {
      return { hasError: true, isReloading: true };
    }

    // For other errors, let them propagate
    return { hasError: true, isReloading: false };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ChunkErrorBoundary caught error:', error, errorInfo);

    // Check if it's a chunk loading error and we should reload
    const isChunkError = 
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk') ||
      error.message.includes('Importing a module script failed');

    if (isChunkError) {
      const reloadKey = 'chunk-error-reload';
      
      // Only reload once to prevent infinite loops
      if (!sessionStorage.getItem(reloadKey)) {
        console.warn('Chunk loading error detected, reloading page...');
        sessionStorage.setItem(reloadKey, Date.now().toString());
        
        // Small delay to show loading state briefly
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        // If we've already reloaded, clear the flag after 10 seconds
        const lastReload = parseInt(sessionStorage.getItem(reloadKey) || '0', 10);
        if (Date.now() - lastReload > 10000) {
          sessionStorage.removeItem(reloadKey);
        }
      }
    }
  }

  render() {
    if (this.state.isReloading) {
      // Show loading spinner while reloading
      return <FullscreenLoading />;
    }

    if (this.state.hasError) {
      // For non-chunk errors, show a simple error message
      return (
        <div className="min-h-screen flex items-center justify-center bg-bg-primary">
          <div className="text-center p-8">
            <span className="material-icons-round text-4xl text-red-400 mb-4 block">error_outline</span>
            <h2 className="text-lg font-semibold text-white mb-2">Something went wrong</h2>
            <p className="text-gray-400 mb-4">Please refresh the page to continue.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-400 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
